import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Avatar, PriorityBadge, ProductTag } from "./widgets";
import { formatCurrency, relativeTime } from "./format";
import { STATUS_COLORS, STATUS_META, STATUS_ORDER, type AdminLead, type LeadStatus } from "./types";

export function Pipeline({
  leads,
  onOpen,
  onMove
}: {
  leads: AdminLead[];
  onOpen: (id: string) => void;
  onMove: (id: string, status: LeadStatus) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<LeadStatus | null>(null);

  const columns = STATUS_ORDER.map((status) => ({
    status,
    items: leads
      .filter((lead) => lead.status === status)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }));

  const drop = (status: LeadStatus) => {
    if (dragId) {
      const dragged = leads.find((lead) => lead.id === dragId);
      if (dragged && dragged.status !== status) onMove(dragId, status);
    }
    setDragId(null);
    setOverCol(null);
  };

  return (
    <div className="crm-view">
      <p className="crm-hint">Przeciągnij kartę między kolumnami albo użyj strzałek, aby zmienić etap.</p>
      <div className="crm-kanban">
        {columns.map((col) => {
          const index = STATUS_ORDER.indexOf(col.status);
          const prev = STATUS_ORDER[index - 1];
          const next = STATUS_ORDER[index + 1];
          return (
            <div
              key={col.status}
              className={`crm-col${overCol === col.status ? " is-over" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                if (overCol !== col.status) setOverCol(col.status);
              }}
              onDragLeave={() => setOverCol((current) => (current === col.status ? null : current))}
              onDrop={() => drop(col.status)}
            >
              <span className="crm-col-accent" style={{ background: STATUS_COLORS[col.status] }} />
              <header className="crm-col-head">
                <span className="crm-col-dot" style={{ background: STATUS_COLORS[col.status] }} />
                <h3>{STATUS_META[col.status].label}</h3>
                <span className="crm-col-count">{col.items.length}</span>
              </header>
              <div className="crm-col-body">
                {col.items.length === 0 && <p className="crm-col-empty">Brak zgłoszeń</p>}
                {col.items.map((lead) => (
                  <article
                    key={lead.id}
                    className={`crm-lead-card${dragId === lead.id ? " is-dragging" : ""}`}
                    draggable
                    onDragStart={() => setDragId(lead.id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setOverCol(null);
                    }}
                    onClick={() => onOpen(lead.id)}
                  >
                    <div className="crm-lead-card-top">
                      <Avatar name={lead.name} size={32} />
                      <div className="crm-lead-card-name">
                        <strong>{lead.name}</strong>
                        <span>{lead.city || "—"}</span>
                      </div>
                      <PriorityBadge priority={lead.priority} compact />
                    </div>
                    <div className="crm-lead-card-mid">
                      <ProductTag product={lead.product} />
                      {lead.budget != null && <span className="crm-lead-card-budget">{formatCurrency(lead.budget)}/mies.</span>}
                    </div>
                    <div className="crm-lead-card-foot">
                      <span className="crm-lead-card-owner">{lead.owner || "Nieprzypisany"}</span>
                      <span>{relativeTime(lead.createdAt)}</span>
                    </div>
                    <div className="crm-lead-card-move" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        disabled={!prev}
                        title="Poprzedni etap"
                        onClick={() => prev && onMove(lead.id, prev)}
                      >
                        <ChevronLeft size={15} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        disabled={!next}
                        title="Następny etap"
                        onClick={() => next && onMove(lead.id, next)}
                      >
                        <ChevronRight size={15} aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
